/* eslint-disable react/forbid-prop-types,react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { MODAL_SCREENS, SALES_TABS } from '../../constants';
import { formatNumber, formatNumberWithComma } from '../../../../utilities/formatHelpers';
import { currencies, exchange } from '../../../../utilities/constants';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import { validateNumbers } from '../../../../utilities/formValidation';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import { RATES_QUERY_KEY } from '../../../../utilities/sharedQueries';
import { dynamicAssets } from '../../../../utilities/helpers';

const Sales = ({ form, openScreen, updateForm, fiatCurrencyCode, activeTab, setActiveTab }) => {
  const { t: translate } = useTranslation();

  const sourceCurrencyCode = form?.source?.currency_code;
  const sourceWalletAvailableBalance = form?.sourceWallet?.available_balance || 0;
  const sourceCurrencyType = form?.source?.currency_type;
  const sourceAmountValue = form?.sourceAmount;
  const destinationCurrencyCode = form?.destination?.currency_code;
  const destinationWalletCoin = form?.destinationWallet?.coin;
  const destinationAmountValue = form?.destinationAmount;
  const destinationCurrencyType = form?.destination?.currency_type;

  const [minEnquiry] = useSetting('otc_configurations', 'otc_minimum_enquiry');
  const { data: rates } = useQuery(RATES_QUERY_KEY);

  const minSourceValue = useMemo(() => {
    if (rates && minEnquiry) {
      const rate =
        rates[`${currencies.USD.fullName}${form?.source?.currency_code?.toUpperCase()}`]?.ask;
      const minEnquiryInSourceCurrency = minEnquiry * rate;

      return minEnquiryInSourceCurrency;
    }

    return 0;
  }, [rates, minEnquiry, form?.source]);

  const minDestinationValue = useMemo(() => {
    if (rates && minEnquiry) {
      const rate =
        rates[`${currencies.USD.fullName}${form?.destination?.currency_code?.toUpperCase()}`]?.ask;
      const minEnquiryInSourceCurrency = minEnquiry * rate;

      return minEnquiryInSourceCurrency;
    }

    return 0;
  }, [rates, minEnquiry, form?.destination]);

  const [sourceAmount, setSourceAmount] = useState(
    sourceAmountValue === 0 ? '' : sourceAmountValue
  );

  const [destinationAmount, setDestinationAmount] = useState(
    destinationAmountValue === 0 ? '' : destinationAmountValue
  );

  const rate = useRate(sourceCurrencyCode, fiatCurrencyCode);
  const destinationFiatRate = useRate(destinationCurrencyCode, fiatCurrencyCode);
  const conversionRate = useRate(sourceCurrencyCode, destinationCurrencyCode);
  const conversionRateReverse = useRate(destinationCurrencyCode, sourceCurrencyCode);
  const sourceInputRef = useRef(null);
  const destinationInputRef = useRef(null);

  useEffect(() => {
    setSourceAmount('');
    setDestinationAmount('');
    updateForm({ ...form, sourceAmount: 0, destinationAmount: 0 });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleSourceAmountInput = (e) => {
    if (!validateNumbers(e.target.value, sourceCurrencyType, false)) {
      return;
    }

    setSourceAmount(e.target.value);
    const newDestinationAmount = e.target.value * conversionRate;

    if (!e.target.value) {
      setDestinationAmount('');
    } else {
      setDestinationAmount(
        formatNumber(newDestinationAmount, currencies[destinationCurrencyCode].decimal)
      );
    }
  };

  const handleDestinationAmountInput = (e) => {
    if (!validateNumbers(e.target.value, destinationCurrencyType, false)) {
      return;
    }

    setDestinationAmount(e.target.value);

    const newSourceAmount = e.target.value * conversionRateReverse;

    if (!e.target.value) {
      setSourceAmount('');
    } else {
      setSourceAmount(formatNumber(newSourceAmount, currencies[sourceCurrencyCode].decimal));
    }
  };

  const disableButton = useMemo(
    () =>
      sourceAmount <= 0 ||
      destinationAmount <= 0 ||
      sourceAmount < minSourceValue ||
      destinationAmount < minDestinationValue,

    [sourceAmount, destinationAmount, minDestinationValue, minSourceValue]
  );

  const clearError = (e) => {
    if (!e.target) {
      e.className = e.className.replace('has-error', '');
    } else {
      e.target.className = e.className.replace('has-error', '');
    }
  };

  useEffect(() => {
    if (sourceInputRef && sourceInputRef.current) {
      if (sourceInputRef.current.value < minSourceValue && sourceAmount) {
        if (sourceInputRef.current.className.indexOf('has-error') === -1) {
          sourceInputRef.current.className += ' has-error';
        }
      } else {
        clearError(sourceInputRef.current);
      }
    }

    if (destinationInputRef && destinationInputRef.current) {
      if (destinationInputRef.current.value < minDestinationValue && sourceAmount) {
        if (destinationInputRef.current.className.indexOf('has-error') === -1) {
          destinationInputRef.current.className += ' has-error';
        }
      } else {
        clearError(destinationInputRef.current);
      }
    }
  }, [
    minSourceValue,
    minDestinationValue,
    sourceAmount,
    destinationAmount,
    sourceInputRef,
    destinationInputRef,
  ]);

  const renderFooter = () => (
    <button
      className={`btn ${!disableButton ? 'btn-primary' : 'btn-disabled'} py-3`}
      type="button"
      disabled={disableButton}
      onClick={() => {
        updateForm({ ...form, sourceAmount, destinationAmount });

        openScreen(MODAL_SCREENS.COMMUNICATE_OPTIONS);
      }}>
      {translate('continue_btn_title')}
    </button>
  );

  const renderTabs = () => (
    <div className="d-flex flex-row flex-fill w-100 mt-3 mb-4">
      <div
        role="button"
        tabIndex="-1"
        onClick={() => {
          setActiveTab(SALES_TABS.BUY);
          updateForm({ ...form, tradeType: SALES_TABS.BUY });
        }}
        onKeyDown={() => {}}
        className={`w-50 px-3 py-3 border-2 border-bottom text-center ${
          activeTab === SALES_TABS.BUY ? 'border-primary' : 'border-disabled'
        }`}>
        <p className="text-dark text-f5">{translate('OTC_sales_modal_buy_tab')}</p>
      </div>

      <div
        role="button"
        tabIndex="-1"
        onClick={() => {
          setActiveTab(SALES_TABS.SELL);
          updateForm({ ...form, tradeType: SALES_TABS.SELL });
        }}
        onKeyDown={() => {}}
        className={`w-50 px-3 py-3 border-2 border-bottom text-center ${
          activeTab === SALES_TABS.SELL ? 'border-primary' : 'border-disabled'
        }`}>
        <p className="text-dark text-f5">{translate('OTC_sales_modal_sell_tab')}</p>
      </div>
    </div>
  );

  const renderSourceSection = () => (
    <>
      <div className="select-group bg-light rounded-extra p-3 d-flex flex-row mb-4">
        <div
          role="button"
          tabIndex="0"
          className="d-flex flex-row align-items-center px-2 card-calculator__select-currency text-hover-dimmed"
          onClick={() => {
            updateForm({
              ...form,

              isOpenSourceCurrencies: true,
            });

            openScreen(MODAL_SCREENS.CURRENCIES);
          }}
          onKeyDown={null}>
          <img
            className="me-2"
            style={{ width: '2rem', height: '2rem' }}
            alt={sourceCurrencyCode?.toUpperCase()}
            src={dynamicAssets(`./assets/icons/${sourceCurrencyCode?.toUpperCase()}.svg`)}
          />

          <p className="m-0 me-2 text-f5 upper-case">{sourceCurrencyCode}</p>

          <i className="icon icon-arrow-down-navigation" />
        </div>

        <span className="text-right flex-fill">
          <b className="text-f5 m-0">
            {formatNumber(
              sourceWalletAvailableBalance *
                currencies[sourceCurrencyCode?.toUpperCase()].conversion,

              currencies[sourceCurrencyCode?.toUpperCase()].decimal
            )}{' '}
            {sourceCurrencyCode?.toUpperCase()}
          </b>

          <p className="text-shadow text-f6 m-0">
            {formatNumberWithComma(
              formatNumber(
                sourceWalletAvailableBalance *
                  currencies[sourceCurrencyCode?.toUpperCase()].conversion *
                  rate,

                exchange.FIAT_VALUE
              )
            )}{' '}
            {currencies[fiatCurrencyCode.toUpperCase()].symbol}
          </p>
        </span>
      </div>
      <div className="form-group mt-2">
        <div className="w-100 position-relative">
          <input
            ref={sourceInputRef}
            placeholder={translate('you_spend_label')}
            className="form-control-input pe-4"
            value={sourceAmount}
            onChange={handleSourceAmountInput}
            name="source"
            type="text"
          />

          <label className="floating-label">{translate('you_spend_label')}</label>
          {sourceAmount < minSourceValue && sourceAmount && (
            <label className="error-label">
              {translate('exchange_min_error_msg').replace(
                '%d',
                `${formatNumber(
                  minSourceValue,
                  currencies[sourceCurrencyCode?.toUpperCase()].decimal
                )} ${sourceCurrencyCode?.toUpperCase()}`
              )}
            </label>
          )}
        </div>
      </div>
    </>
  );

  const renderDestinationSection = () => (
    <>
      <div className="select-group bg-light rounded-extra p-3 d-flex flex-row mb-4">
        <div
          role="button"
          tabIndex="0"
          className="d-flex flex-row align-items-center px-2 card-calculator__select-currency text-hover-dimmed"
          onClick={() => {
            updateForm({
              ...form,
              isOpenSourceCurrencies: false,
            });

            openScreen(MODAL_SCREENS.CURRENCIES);
          }}
          onKeyDown={null}>
          <img
            className="me-2"
            style={{ width: '2rem', height: '2rem' }}
            alt={destinationCurrencyCode?.toUpperCase()}
            src={dynamicAssets(`./assets/icons/${destinationCurrencyCode?.toUpperCase()}.svg`)}
          />

          <p className="m-0 me-2 text-f5 upper-case">{destinationCurrencyCode}</p>

          <i className="icon icon-arrow-down-navigation" />
        </div>

        <span className="text-right flex-fill">
          <b className="text-f5 m-0">
            {formatNumber(
              (form?.destinationWallet?.available_balance || 0) *
                currencies[destinationCurrencyCode?.toUpperCase()].conversion,

              currencies[destinationCurrencyCode?.toUpperCase()].decimal
            )}{' '}
            {destinationCurrencyCode?.toUpperCase()}
          </b>

          <p className="text-shadow text-f6 m-0">
            {formatNumberWithComma(
              formatNumber(
                (form?.destinationWallet?.available_balance || 0) *
                  currencies[destinationWalletCoin?.toUpperCase()].conversion *
                  destinationFiatRate,

                exchange.FIAT_VALUE
              )
            )}{' '}
            {currencies[fiatCurrencyCode.toUpperCase()].symbol}
          </p>
        </span>
      </div>
      <div className="form-group mt-2">
        <div className="w-100 position-relative">
          <input
            ref={destinationInputRef}
            placeholder={translate('OTC_you_get_label')}
            className="form-control-input pe-4"
            value={destinationAmount}
            onChange={handleDestinationAmountInput}
            name="destination"
            type="text"
          />

          <label className="floating-label">{translate('OTC_you_get_label')}</label>
          {destinationAmount < minDestinationValue && destinationAmount && (
            <label className="error-label">
              {translate('exchange_min_error_msg').replace(
                '%d',
                `${formatNumber(
                  minDestinationValue,
                  currencies[destinationCurrencyCode?.toUpperCase()].decimal
                )} ${destinationCurrencyCode?.toUpperCase()}`
              )}
            </label>
          )}
        </div>
      </div>
    </>
  );

  const destinationComponent = useMemo(
    () => (activeTab === SALES_TABS.BUY ? renderDestinationSection() : renderSourceSection()),
    [activeTab, sourceAmount, destinationAmount]
  );

  const sourceComponent = useMemo(
    () => (activeTab === SALES_TABS.BUY ? renderSourceSection() : renderDestinationSection()),
    [activeTab, sourceAmount, destinationAmount]
  );

  return (
    <ModalBodyLayout
      title={translate('OTC_sales_modal_title')}
      withBack={false}
      renderFooter={renderFooter}
      isScrollable>
      {renderTabs()}
      {destinationComponent}
      <div className="d-flex flex-row justify-content-center align-items-center my-5">
        <div
          className="text-white text-f5 border border-1 border-shadow rounded-max d-flex justify-content-center align-items-center"
          style={{
            paddingTop: '0.75rem',
            paddingBottom: '0.75rem',
            paddingRight: '0.8rem',
            paddingLeft: '0.8rem',
          }}>
          <img src="./assets/icons/arrow-down.svg" alt="check" />
        </div>
        <p className="text-f6 text-shadow ms-2">
          {activeTab === SALES_TABS.BUY ? translate('OTC_with_label') : translate('OTC_for_label')}
        </p>
      </div>
      {sourceComponent}
    </ModalBodyLayout>
  );
};

export default Sales;

Sales.propTypes = {
  form: PropTypes.object.isRequired,
  openScreen: PropTypes.func.isRequired,
  updateForm: PropTypes.func.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  activeTab: PropTypes.string.isRequired,
  fiatCurrencyCode: PropTypes.string.isRequired,
};
